import {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {useIntl} from "react-intl";
import {ArrowUpRight, Loader2, MessageSquare, MoreHorizontal, Phone, Sparkles, Voicemail, AudioLines} from "lucide-react";
import {DsAvatar, PhoneHQDropdown} from "ch3-ui-lib";
import {duration, flatDurationInMinutes} from "../../utilis/timeUtilis";
import {getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";
import {getFullName} from "../../utilis/getFullName.ts";
import {isGroupCall} from "../../utilis/callsUtilis";
import {ClickToCall} from "../../components/ClickToCall/ClickToCall";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";
import {isRecordingDeleted, RECORDING_DELETED_BODY} from "./recordingState";
import {
    Call,
    useFetchCallRecordQuery,
    useFetchCallSummaryQuery,
    useFetchCallTranscriptQuery,
    useRequestCallSummaryMutation,
    useRequestCallTranscriptionMutation
} from "../../store/features/calls/callsService";
import ContactPanel from "../Contacts/ContactPanel.tsx";
import {resolveAiInsightsState} from "./aiInsightsState.ts";
import {useIsChatEnabledQuery} from "../../services/chatService.ts";
import {useOpenDirectChat} from "../../hooks/useOpenDirectChat.ts";
import {callRowView} from "./callRowView";
import {CallerAvatar, chip, DirectionBadge, formatCallDate, InfoRow, InternalBadge, lucide, NumberFlag, SpamBadge, whiteIconButton} from "./callParts";
import {ReportCallProblem} from "./ReportCallProblem";
import GroupCallDetails from "./GroupCallDetails";
import {useInsightsDrawerParams} from "./insights/useInsightsDrawerParams";
import {ReasonChip, SentimentChip} from "./insights/summary/InsightChips";
import {isContentFiltered} from "./insights/insightsErrors";
import {insightsMessages} from "./insights/insightsMessages";

interface CallDetailsProps {
    selectedCall: Call,
}

const tintButton = 'inline-flex h-8 cursor-pointer items-center gap-2 rounded-control bg-surface px-3 text-footnote font-semibold text-brand-ink hover:bg-brand-tint-subtle';

const CallDetails = ({selectedCall}: CallDetailsProps) =>
    isGroupCall(selectedCall)
        ? <GroupCallDetails selectedCall={selectedCall}/>
        : <SingleCallDetails selectedCall={selectedCall}/>;

const SingleCallDetails = ({selectedCall}: CallDetailsProps) => {
    const view = callRowView(selectedCall);
    const callId = useMemo(() => selectedCall?.callId, [selectedCall?.callId]);
    const recordingId = useMemo(() => selectedCall?.recordingId, [selectedCall?.recordingId]);

    const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
    const [createContactPanelOpen, setCreateContactPanelOpen] = useState(false);
    const [aiError, setAiError] = useState<unknown>(null);
    const {data: fetchedRecordingUrl, error: recordingError} = useFetchCallRecordQuery(Number(recordingId), {skip: !recordingId});
    const recordingDeleted = !!recordingId && isRecordingDeleted(recordingError);

    const {data: transcript, isFetching: isFetchingTranscript, error: transcriptError} = useFetchCallTranscriptQuery(Number(callId), {skip: !callId});
    const {data: summary, isFetching: isFetchingSummary, error: summaryError} = useFetchCallSummaryQuery(Number(callId), {skip: !callId});

    const [generateSummaryMutation, {isLoading: isGeneratingSummary}] = useRequestCallSummaryMutation();
    const [generateTranscriptMutation, {isLoading: isGeneratingTranscript}] = useRequestCallTranscriptionMutation();

    const hasTranscript = !!transcript && !transcriptError;
    const hasSummary = !!summary && !summaryError;
    const aiInsightsState = resolveAiInsightsState({hasTranscript, hasSummary, hasRecording: Boolean(recordingId) && !recordingDeleted});
    const isGenerating = isGeneratingSummary || isGeneratingTranscript;
    const isJustFetching = (isFetchingTranscript || isFetchingSummary) && !isGenerating;

    // Calls longer than 15 minutes are not transcribed.
    const isCallWithTranscript = Boolean(
        flatDurationInMinutes(selectedCall?.bridgetAt, selectedCall?.finishedAt) < 15
        || flatDurationInMinutes(selectedCall?.forwardedAt, selectedCall?.finishedAt) < 15
        || (selectedCall?.hasVoicemail && flatDurationInMinutes(selectedCall?.startedAt, selectedCall?.finishedAt) < 15)
    );

    const intl = useIntl();
    const insightsDrawer = useInsightsDrawerParams();
    const {data: isChatEnabled} = useIsChatEnabledQuery();
    const openDirectChat = useOpenDirectChat();

    useEffect(() => {
        setRecordingUrl(recordingId && fetchedRecordingUrl ? fetchedRecordingUrl : null);
    }, [recordingId, fetchedRecordingUrl]);

    const generateMissingAiArtifact = async () => {
        if (!callId || !aiInsightsState.action) return;
        setAiError(null);
        try {
            if (aiInsightsState.action.request === "transcript") {
                await generateTranscriptMutation({callId}).unwrap();
            } else {
                await generateSummaryMutation({callId}).unwrap();
            }
        } catch (error) {
            setAiError(error);
        }
    };
    const aiFailed = aiError !== null;
    const aiFiltered = isContentFiltered(aiError);

    const isVoicemail = Boolean(selectedCall.hasVoicemail && selectedCall.type === 'INBOUND' && !selectedCall.forwardedTo);
    const insightChips = selectedCall.insights && (selectedCall.insights.reason || selectedCall.insights.sentiment) ? (
        <div className="flex flex-wrap items-center gap-1.5 px-1" data-testid="call-details-insight-chips">
            {selectedCall.insights.sentiment && <SentimentChip sentiment={selectedCall.insights.sentiment}/>}
            {selectedCall.insights.reason && <ReasonChip reason={selectedCall.insights.reason}/>}
        </div>
    ) : null;
    const callTarget = selectedCall.internalCallee ? selectedCall.internalCallee.userId : selectedCall.clientMsisdn;
    const contact = selectedCall.primaryContact && !selectedCall.internalCallee ? selectedCall.primaryContact : null;
    const isPipedrive = contact?.contactType?.source === 'PIPEDRIVE';
    const canCreateContact = !contact && !selectedCall.internalCallee && Boolean(selectedCall.clientMsisdn);

    const aiAction = () => {
        if (isGenerating) {
            return <span className="inline-flex h-8 items-center gap-2 rounded-control bg-surface px-3 text-footnote text-ink-muted"><Loader2 size={14} aria-hidden="true" className={`${lucide} animate-spin`}/>Generating AI insights…</span>;
        }
        if (isJustFetching) {
            return <Loader2 size={16} aria-label="Loading AI insights" className={`${lucide} animate-spin text-brand-ink`}/>;
        }
        if (aiFiltered) {
            return null;
        }
        if (aiFailed && aiInsightsState.action) {
            return <button type="button" onClick={generateMissingAiArtifact} className={tintButton}>Try again</button>;
        }
        if (aiInsightsState.canView) {
            return (
                <button type="button" onClick={(event) => insightsDrawer.open(selectedCall.callId, {entry: 'call_details', returnFocus: event.currentTarget})} className={tintButton}>
                    <Sparkles size={14} aria-hidden="true" className={lucide}/>Show AI insights
                </button>
            );
        }
        if (aiInsightsState.action && isCallWithTranscript) {
            return (
                <button type="button" onClick={generateMissingAiArtifact} className={tintButton}>
                    <Sparkles size={14} aria-hidden="true" className={lucide}/>Generate AI insights
                </button>
            );
        }
        return null;
    };

    return (
        <div className="flex flex-col gap-4 px-8 py-6" data-testid="call-details">
            {/* Header: who, their number, and what you can do */}
            <div className="flex items-center gap-4">
                <CallerAvatar call={selectedCall} view={view} size={48}/>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex min-w-0 items-center gap-2">
                        <h1 className="m-0 truncate text-headline text-ink">{view.name}</h1>
                        {view.spam && <SpamBadge/>}
                        {view.internal && <InternalBadge/>}
                    </div>
                    {selectedCall.clientMsisdn && !selectedCall.internalCallee && view.known && (
                        <div>
                            <span className={chip}>
                                <NumberFlag msisdn={selectedCall.clientMsisdn}/>
                                {getFormatedPhoneNumber(selectedCall.clientMsisdn, true)}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {callTarget && (
                        <ClickToCall
                            renderAs="button"
                            msisdn={Number(callTarget)}
                            contact={selectedCall.internalCallee ?? selectedCall.primaryContact}
                            internal={Boolean(selectedCall.internalCallee)}
                            className="inline-flex h-control-md cursor-pointer items-center gap-2 rounded-control bg-brand px-4 text-callout font-medium text-white hover:bg-brand-hover"
                        >
                            <Phone size={16} aria-hidden="true" className={lucide}/>
                            Call
                        </ClickToCall>
                    )}
                    {selectedCall.internalCallee && isChatEnabled !== false && (
                        <button
                            type="button"
                            onClick={() => selectedCall.internalCallee && openDirectChat(selectedCall.internalCallee.userId)}
                            aria-label={intl.formatMessage({id: 'calls.details.chat'})}
                            className={whiteIconButton}
                        >
                            <MessageSquare size={18} aria-hidden="true" className={lucide}/>
                        </button>
                    )}
                    {(canCreateContact || contact) && (
                        <PhoneHQDropdown
                            align="end"
                            sideOffset={4}
                            trigger={
                                <button type="button" aria-label="More" aria-haspopup="menu" className={whiteIconButton}>
                                    <MoreHorizontal size={18} aria-hidden="true" className={lucide}/>
                                </button>
                            }
                        >
                            {canCreateContact && (
                                <PhoneHQDropdown.Item onClick={() => setCreateContactPanelOpen(true)}>
                                    <span className="px-3 text-callout">Create new contact</span>
                                </PhoneHQDropdown.Item>
                            )}
                            {contact && !isPipedrive && (
                                <Link to={`/contacts/details/${contact.contactId}`}>
                                    <PhoneHQDropdown.Item>
                                        <span className="px-3 text-callout">View contact</span>
                                    </PhoneHQDropdown.Item>
                                </Link>
                            )}
                            {isPipedrive && contact?.url && (
                                <a href={contact.url} target="_blank" rel="noreferrer">
                                    <PhoneHQDropdown.Item>
                                        <span className="inline-flex items-center gap-1 px-3 text-callout">Edit in source system <ArrowUpRight size={14} aria-hidden="true" className={lucide}/></span>
                                    </PhoneHQDropdown.Item>
                                </a>
                            )}
                        </PhoneHQDropdown>
                    )}
                </div>
            </div>

            {/* Recording or voicemail, with AI insights */}
            {recordingUrl && (
                <section aria-label={isVoicemail ? 'Voicemail' : 'Recording'} className="flex flex-col gap-3 rounded-card bg-brand-tint p-3">
                    <div className="flex items-center justify-between gap-2 px-1">
                        <span className="inline-flex items-center gap-2 text-callout font-semibold text-brand-ink">
                            {isVoicemail
                                ? <Voicemail size={16} aria-hidden="true" className={lucide}/>
                                : <AudioLines size={16} aria-hidden="true" className={lucide}/>}
                            {isVoicemail ? 'Voicemail' : 'Recording'}
                        </span>
                        {aiAction()}
                    </div>
                    {insightChips}
                    <div className="rounded-control bg-surface px-3 py-1">
                        <AudioPlayer
                            disabled={!recordingUrl}
                            src={recordingUrl}
                            filename={(isVoicemail ? selectedCall.startedAt : selectedCall.bridgetAt) + '_' + selectedCall.clientMsisdn}
                        />
                    </div>
                </section>
            )}
            {recordingDeleted && (
                <section aria-label="Recording" data-testid="call-recording-deleted" className="flex flex-col gap-3 rounded-card bg-brand-tint p-3">
                    <div className="flex items-center justify-between gap-2 px-1">
                        <span className="inline-flex items-center gap-2 text-callout font-semibold text-brand-ink">
                            <AudioLines size={16} aria-hidden="true" className={lucide}/>
                            Recording
                        </span>
                        {aiAction()}
                    </div>
                    {insightChips}
                    <div className="rounded-control bg-surface px-3 py-2.5">
                        <p className="text-callout font-semibold text-ink">Recording deleted</p>
                        <p className="mt-0.5 text-footnote text-ink-muted">{RECORDING_DELETED_BODY}</p>
                    </div>
                </section>
            )}
            {!recordingUrl && !recordingDeleted && (aiInsightsState.canView || (aiInsightsState.action && !aiInsightsState.unavailableReason)) && (
                <section aria-label="AI insights" className="flex flex-col gap-3 rounded-card bg-brand-tint p-3">
                    <div className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-2 px-1 text-callout font-semibold text-brand-ink">
                            <Sparkles size={16} aria-hidden="true" className={lucide}/>AI insights
                        </span>
                        {aiAction()}
                    </div>
                    {insightChips}
                </section>
            )}
            {aiFailed && (
                <div role="alert" className="text-footnote text-danger-ink">
                    {aiFiltered ? intl.formatMessage(insightsMessages.contentFiltered) : 'We could not generate AI insights. Your recording is safe.'}
                </div>
            )}

            {/* Facts about the call */}
            <section aria-label="Call information" className="rounded-card bg-surface-muted px-1 pb-1">
                <div className="flex h-10 items-center gap-2 px-3 text-callout font-semibold text-ink">
                    <DirectionBadge view={view}/>
                </div>
                <div className="rounded-control bg-surface">
                    {selectedCall.startedAt && <InfoRow label="Date">{formatCallDate(selectedCall.startedAt)}</InfoRow>}
                    {selectedCall.bridgetAt && selectedCall.finishedAt && (
                        <InfoRow label="Call length">{duration(selectedCall.bridgetAt, selectedCall.finishedAt).trim()}</InfoRow>
                    )}
                    {Boolean(selectedCall.bridgetAt || selectedCall.type !== 'INBOUND') && selectedCall.user && (
                        <InfoRow label="Handled by">
                            <span className={chip}>
                                <DsAvatar size={20} name={getFullName(selectedCall.user.firstName, selectedCall.user.lastName)} src={selectedCall.user.avatarUrl || undefined}/>
                                {getFullName(selectedCall.user.firstName, selectedCall.user.lastName)}
                            </span>
                        </InfoRow>
                    )}
                    {selectedCall.forwardedTo && (
                        <InfoRow label="Forwarded to">
                            <span className={chip}>
                                <NumberFlag msisdn={selectedCall.forwardedTo}/>
                                {getFormatedPhoneNumber(selectedCall.forwardedTo, true)}
                            </span>
                        </InfoRow>
                    )}
                    {selectedCall.exposedMsisdn && (
                        <InfoRow label="Line">
                            <span className={chip}>
                                <NumberFlag msisdn={selectedCall.exposedMsisdn.msisdn}/>
                                {selectedCall.exposedMsisdn.label} {getFormatedPhoneNumber(selectedCall.exposedMsisdn.msisdn, true)}
                            </span>
                        </InfoRow>
                    )}
                    {selectedCall.internalCallee && <InfoRow label="Line">Internal call</InfoRow>}
                </div>
            </section>

            <ReportCallProblem callData={{
                callType: selectedCall.type,
                callId: selectedCall.callId,
                externalMsisdn: selectedCall.clientMsisdn,
                phoneNumber: selectedCall.exposedMsisdn?.msisdn,
                callLength: duration(selectedCall.bridgetAt, selectedCall.finishedAt),
                callStart: selectedCall.bridgetAt,
                platform: selectedCall.platform,
            }}/>

            <ContactPanel
                open={createContactPanelOpen}
                onClose={() => setCreateContactPanelOpen(false)}
                initialPhoneNumber={String(selectedCall.clientMsisdn)}
            />
        </div>
    );
};

export default CallDetails;
